'use client'

/**
 * Unified media lightbox component.
 * - MediaLightbox       — desktop: arrows, counter, keyboard nav, subtitles, AI video
 * - MobileMediaLightbox — mobile:  fully DOM-driven carousel + zoom, no React state for positions
 * - MobileVideoPlayer   — mobile: custom video player with seek bar, subtitles, auto-hide controls
 */

import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { X, ChevronLeft, ChevronRight, RotateCcw, RotateCw } from 'lucide-react'
import { getVisibleSubtitleWords, type AIVideoMetadata, type SubtitleWord } from './scenario-step-attachment'
import { DesktopVideoPlayer } from './desktop-video-player'
import { getAsset } from '@/lib/api-v3'
import { getTokens } from '@/lib/auth'
import { VIDEO_BLANK_POSTER } from '@/lib/utils'

// ── Public types ───────────────────────────────────────────────────────────────

export interface MediaLightboxItem {
  id?: number
  src: string
  type: 'image' | 'video'
  alt?: string
  subtitlesId?: number | null
  /** Прямая ссылка на JSON субтитров ({words}) — для файлов вне ассетов API (озвучка видео) */
  subtitlesUrl?: string | null
  aiMetadata?: AIVideoMetadata
}

// ── Subtitle loading ───────────────────────────────────────────────────────────

function useSubtitles(item: MediaLightboxItem | undefined) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SubtitleWord[] | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    abortRef.current?.abort()
    setData(null)
    setLoading(false)
    const sid = item?.subtitlesId
    const directUrl = item?.subtitlesUrl
    if (!sid && !directUrl) return
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)
    const token = getTokens()?.accessToken
    ;(async () => {
      try {
        let url = directUrl ?? null
        if (!url && sid) url = (await getAsset(token, sid, controller.signal)).url ?? null
        if (controller.signal.aborted || !url) return
        const resp = await fetch(url, { signal: controller.signal })
        if (controller.signal.aborted || !resp.ok) return
        const parsed = JSON.parse(await resp.text())
        const words: SubtitleWord[] = Array.isArray(parsed.words) ? parsed.words : []
        if (!controller.signal.aborted) setData(words)
      } catch { /* silent */ } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [item?.id, item?.subtitlesId, item?.subtitlesUrl])

  return { loading, data }
}

// ── Shared media renderer ──────────────────────────────────────────────────────

/**
 * Шторка Radix ставит `body { pointer-events: none }` и закрывается, когда фокус
 * уходит из её панели. Просмотрщик — портал на body, поэтому без своего
 * `pointer-events` клик проходит сквозь стрелки и крестик.
 * pointerdown здесь не глушим: ползунок перемотки слушает его через делегирование
 * React, и stopPropagation на слое до document это событие съедает. Шторку
 * удерживает onPointerDownOutside у карточки.
 */
function keepSheetOpen(node: HTMLElement | null) {
  if (!node) return () => {}
  const stop = (event: Event) => event.stopPropagation()
  node.addEventListener('focusin', stop)
  return () => {
    node.removeEventListener('focusin', stop)
  }
}

/** Куда вернуть фокус, чтобы шторка не сочла закрытие просмотра уходом фокуса наружу. */
function focusOutsideLightbox(): HTMLElement | null {
  const active = document.activeElement
  const lightbox = document.querySelector('[data-media-lightbox="true"]')
  if (!(active instanceof HTMLElement) || (lightbox && lightbox.contains(active))) return null
  return active
}

function closeOnEscape(event: KeyboardEvent, onClose: () => void, returnFocus: HTMLElement | null) {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  if (returnFocus && document.contains(returnFocus)) {
    returnFocus.focus()
  } else {
    const sheet = document.querySelector('[data-slot="sheet-content"]')
    if (sheet instanceof HTMLElement) sheet.focus()
  }
  onClose()
}

function MediaContent({
  item,
  subLoading,
  subData,
  className,
}: {
  item: MediaLightboxItem
  subLoading: boolean
  subData: SubtitleWord[] | null
  className?: string
}) {
  const cls = className ?? 'max-w-full max-h-full object-contain pointer-events-auto'
  if (item.type === 'image') {
    return <img src={item.src} alt={item.alt ?? ''} className={cls} draggable={false} />
  }
  if (item.type === 'video') {
    if (subLoading && item.subtitlesId) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 text-white pointer-events-auto">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          <span className="text-sm text-white/70">Загрузка субтитров...</span>
        </div>
      )
    }
    return (
      <DesktopVideoPlayer
        src={item.src}
        aiMetadata={item.aiMetadata}
        subtitlesWords={subData}
        className={cls}
      />
    )
  }
  return null
}

// ── Desktop: MediaLightbox ─────────────────────────────────────────────────────

export const MediaLightbox = memo(function MediaLightbox({
  items,
  initialIndex = 0,
  onClose,
}: {
  items: MediaLightboxItem[]
  initialIndex?: number
  onClose: () => void
}) {
  const [index, setIndex] = useState(initialIndex)
  const rootRef = useRef<HTMLDivElement>(null)
  const current = items[index]
  const { loading: subLoading, data: subData } = useSubtitles(current)

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    returnFocusRef.current = focusOutsideLightbox()
    return keepSheetOpen(rootRef.current)
  }, [])

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeOnEscape(e, () => onCloseRef.current(), returnFocusRef.current)
      } else if (e.key === 'ArrowLeft') {
        setIndex(i => (i - 1 + items.length) % items.length)
      } else if (e.key === 'ArrowRight') {
        setIndex(i => (i + 1) % items.length)
      }
    }
    // На window, фаза capture — раньше Radix (document, capture), как в отчёте по процедурам.
    window.addEventListener('keydown', handle, { capture: true })
    return () => window.removeEventListener('keydown', handle, { capture: true })
  }, [items.length])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  if (typeof document === 'undefined' || !current) return null

  const goPrev = (e: React.MouseEvent) => { e.stopPropagation(); setIndex(i => (i - 1 + items.length) % items.length) }
  const goNext = (e: React.MouseEvent) => { e.stopPropagation(); setIndex(i => (i + 1) % items.length) }
  const keepOpen = (e: React.SyntheticEvent) => e.stopPropagation()

  return createPortal(
    <div
      ref={rootRef}
      data-media-lightbox="true"
      className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center bg-black/85"
      onClick={onClose}
      onPointerDown={keepOpen}
    >
      <button type="button" onPointerDown={keepOpen} onClick={(e) => { e.stopPropagation(); onClose() }} className="absolute top-5 right-5 z-20 cursor-pointer p-2 text-white transition-opacity hover:opacity-75" aria-label="Закрыть">
        <X className="h-8 w-8" />
      </button>
      {items.length > 1 && (
        <button type="button" onPointerDown={keepOpen} onClick={goPrev} className="absolute left-5 top-1/2 z-20 -translate-y-1/2 cursor-pointer p-2 text-white transition-opacity hover:opacity-75" aria-label="Назад">
          <ChevronLeft className="h-12 w-12" />
        </button>
      )}
      <div className="w-[80%] h-[80%] flex items-center justify-center z-10 pointer-events-none" onClick={e => e.stopPropagation()}>
        <MediaContent item={current} subLoading={subLoading} subData={subData} />
      </div>
      {items.length > 1 && (
        <button type="button" onPointerDown={keepOpen} onClick={goNext} className="absolute right-5 top-1/2 z-20 -translate-y-1/2 cursor-pointer p-2 text-white transition-opacity hover:opacity-75" aria-label="Вперёд">
          <ChevronRight className="h-12 w-12" />
        </button>
      )}
      {items.length > 1 && (
        <div className="pointer-events-none absolute bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white">
          {index + 1} / {items.length}
        </div>
      )}
    </div>,
    document.body
  )
})

// ── Hook: useMobileVideoPlayer ─────────────────────────────────────────────────
//
// Manages all video state and handlers. Separated from rendering so that:
//  • The <video> element can live inside zoomRef (for pinch-zoom on the video itself)
//  • The controls live OUTSIDE zoomRef (so they don't scale with pinch-zoom)

/** Абсолютные [start, end] секунды AI-обрезанного фрагмента — та же формула, что в AIVideoPlayer. */
function computeAiWindow(m: AIVideoMetadata): { start: number; end: number } {
  const start = m.overwrittenStart != null ? m.overwrittenStart : Math.max(0, m.start - 0.5)
  const end = m.overwrittenEnd != null
    ? m.overwrittenEnd
    : (m.nextStartTime > 0 ? m.nextStartTime - 0.5 : m.end)
  return { start, end }
}

type VideoPlayerState = ReturnType<typeof useMobileVideoPlayer>

function useMobileVideoPlayer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  itemIndex: number,
  // AI-обрезанный фрагмент внутри исходного видео (абсолютные секунды). Когда задан —
  // currentTime/duration/skip/seek работают в системе координат [aiWindow.start, aiWindow.end],
  // а не всего файла — так AI-вложения играют в едином мобильном плеере, а не в старом AIVideoPlayer.
  aiWindow?: { start: number; end: number } | null,
) {
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isPlaying,    setIsPlaying]    = useState(false)
  const [isLoading,    setIsLoading]    = useState(true)   // true until canplay/playing
  const [currentTime,  setCurrentTime]  = useState(0)
  // Абсолютное время в исходном файле — нужно для сопоставления субтитров (они индексированы
  // по абсолютному времени, даже когда currentTime показывает время относительно aiWindow.start).
  const [absoluteTime, setAbsoluteTime] = useState(0)
  const [duration,     setDuration]     = useState(0)
  const [showControls, setShowControls] = useState(true)
  const [showCaptions, setShowCaptions] = useState(true)
  const [speed,        setSpeed]        = useState(1)

  const SPEEDS = useMemo(() => [0.5, 1, 1.5, 2], [])

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false)
      hideTimerRef.current = null   // mark as "no active timer" so resetHideTimer knows
    }, 3000)
  }, [])

  // Reveal controls and start the auto-hide countdown
  const revealControls = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    setShowControls(true)
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false)
      hideTimerRef.current = null
    }, 3000)
  }, [])

  // Resets the 3-second countdown; only acts when a timer is actually running
  // (= controls are visible). Called on every touchstart AND touchmove by the FSM.
  const resetHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) scheduleHide()
  }, [scheduleHide])

  useEffect(() => () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current) }, [])

  // Reset UI state on slide change (itemIndex = committed React index)
  useEffect(() => {
    // Cancel any pending hide timer from the previous video so it can't fire after navigation
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    setIsPlaying(false); setIsLoading(true); setCurrentTime(0); setAbsoluteTime(0); setDuration(0)
    setShowControls(true); setSpeed(1)
    const v = videoRef.current
    if (v) { v.playbackRate = 1 }
    return () => { videoRef.current?.pause() }
  }, [itemIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // Video event listeners (re-attach on slide change; videoRef.current points to the new <video> by now)
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    // Guard helper — returns true if this video element is still the active one.
    // videoRef.current is nulled by navigate() immediately when leaving a panel,
    // so any pending browser media events that fire during the slide animation
    // (e.g. loadedmetadata → auto-play) are suppressed at this point.
    const isActive = () => v === videoRef.current
    const winStart = aiWindow?.start ?? 0

    const onMeta    = () => {
      if (aiWindow) {
        v.currentTime = winStart
        setDuration(Math.max(0, aiWindow.end - winStart))
      } else {
        setDuration(v.duration)
      }
      // Сбрасываем isLoading как только метаданные загружены — на мобильных браузерах
      // canplay может не срабатывать до явного взаимодействия (autoplay policy),
      // что оставляло спиннер вечно и скрывало кнопку Play.
      if (isActive()) setIsLoading(false)
      // Level 2 guard: only auto-play if this video is still the active panel
      if (isActive()) v.play().catch(() => {})
    }
    const onError   = () => { if (isActive()) setIsLoading(false) }
    const onTime    = () => {
      if (aiWindow) {
        const abs = v.currentTime
        setAbsoluteTime(abs)
        if (abs >= aiWindow.end) {
          // Достигли конца AI-фрагмента — останавливаемся и отматываем к началу
          // (весь остальной файл вне фрагмента пользователю не показываем).
          v.pause()
          v.currentTime = winStart
          setCurrentTime(0)
          setAbsoluteTime(winStart)
          return
        }
        setCurrentTime(Math.max(0, abs - winStart))
      } else {
        setCurrentTime(v.currentTime)
        setAbsoluteTime(v.currentTime)
      }
    }
    const onPlay    = () => {
      // Level 3 guard: if this video fired 'play' but is no longer active, stop it
      if (!isActive()) { try { v.pause() } catch(_) {}; return }
      setIsPlaying(true); setIsLoading(false); scheduleHide()
    }
    const onPause   = () => { setIsPlaying(false); revealControls() }
    const onEnded   = () => {
      setIsPlaying(false)
      if (aiWindow) { v.currentTime = winStart; setCurrentTime(0); setAbsoluteTime(winStart) }
      else { setCurrentTime(0); v.currentTime = 0 }
      revealControls()
    }
    const onWaiting = () => { if (isActive()) setIsLoading(true) }   // mid-play stall
    const onCanPlay = () => { if (isActive()) setIsLoading(false) }  // enough data to play
    v.addEventListener('loadedmetadata', onMeta)
    v.addEventListener('timeupdate',     onTime)
    v.addEventListener('play',           onPlay)
    v.addEventListener('pause',          onPause)
    v.addEventListener('ended',          onEnded)
    v.addEventListener('waiting',        onWaiting)
    v.addEventListener('canplay',        onCanPlay)
    v.addEventListener('error',          onError)
    if (v.readyState >= 1) {
      if (aiWindow) { v.currentTime = winStart; setDuration(Math.max(0, aiWindow.end - winStart)) }
      else { setDuration(v.duration) }
      setIsLoading(false)
      v.play().catch(() => {})
    }
    if (v.readyState >= 3) { setIsLoading(false) }   // HAVE_FUTURE_DATA — already buffered enough
    return () => {
      v.removeEventListener('loadedmetadata', onMeta)
      v.removeEventListener('timeupdate',     onTime)
      v.removeEventListener('play',           onPlay)
      v.removeEventListener('pause',          onPause)
      v.removeEventListener('ended',          onEnded)
      v.removeEventListener('waiting',        onWaiting)
      v.removeEventListener('canplay',        onCanPlay)
      v.removeEventListener('error',          onError)
    }
  }, [itemIndex, scheduleHide, revealControls, aiWindow]) // videoRef is a stable ref object

  const togglePlay = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      if (aiWindow && v.currentTime >= aiWindow.end) v.currentTime = aiWindow.start
      v.play().catch(() => {}); scheduleHide()
    }
    else          { v.pause() }
  }, [videoRef, scheduleHide, aiWindow])

  const skip = useCallback((sec: number) => {
    const v = videoRef.current
    if (!v) return
    const lo = aiWindow?.start ?? 0
    const hi = aiWindow?.end ?? (v.duration || Infinity)
    v.currentTime = Math.max(lo, Math.min(v.currentTime + sec, hi))
    revealControls()
    if (!v.paused) scheduleHide()
  }, [videoRef, revealControls, scheduleHide, aiWindow])

  // Tap on video area: ONLY show/hide controls — does NOT toggle play/pause
  const handleVideoAreaTap = useCallback(() => {
    if (!showControls) {
      revealControls()
    } else {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      setShowControls(false)
    }
  }, [showControls, revealControls])

  const cycleSpeed = useCallback((speeds: number[]) => {
    const v = videoRef.current
    if (!v) return
    const idx  = speeds.indexOf(speed)
    const next = speeds[(idx + 1) % speeds.length]
    v.playbackRate = next
    setSpeed(next)
    revealControls()
    if (!v.paused) scheduleHide()
  }, [videoRef, speed, revealControls, scheduleHide])

  const doSeek = useCallback((clientX: number, barEl: HTMLElement) => {
    const v = videoRef.current
    if (!v) return
    const lo  = aiWindow?.start ?? 0
    const dur = aiWindow ? Math.max(0, aiWindow.end - lo) : v.duration
    if (!isFinite(dur) || dur === 0) return
    const rect = barEl.getBoundingClientRect()
    const pos  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    v.currentTime = lo + pos * dur
    setCurrentTime(pos * dur)
    revealControls()
    if (!v.paused) scheduleHide()
  }, [videoRef, revealControls, scheduleHide, aiWindow])

  const toggleCaptions = useCallback(() => setShowCaptions(v => !v), [])

  return {
    isPlaying, isLoading, currentTime, absoluteTime, duration, showControls, showCaptions, speed, SPEEDS,
    togglePlay, skip, handleVideoAreaTap, cycleSpeed, doSeek, toggleCaptions, resetHideTimer, revealControls,
  }
}

// ── SkipIcon (module-level so carousel panels can use it too) ─────────────────
function SkipIcon({ n, forward }: { n: number; forward: boolean }) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44 }}>
      {forward
        ? <RotateCw size={38} color="white" strokeWidth={2} />
        : <RotateCcw size={38} color="white" strokeWidth={2} />}
      <span style={{
        position: 'absolute',
        top: '54%', left: '50%', transform: 'translate(-50%, -50%)',
        fontSize: 11, fontWeight: 700, color: 'white', lineHeight: 1,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        userSelect: 'none',
      }}>{n}</span>
    </div>
  )
}

// ── MobileVideoControls ────────────────────────────────────────────────────────
//
// Only the BOTTOM seek-bar row. Play/skip/spinner live INSIDE each carousel panel
// so they move naturally with left-right swipes and up-down dismissal.
// This component lives in overlayRef (outside dismissWrapRef) so it fades on
// dismiss without moving, and fades during horizontal swipe to non-video panels.
// display:none keeps DOM/refs/native listeners alive when controls are hidden.

function MobileVideoControls({
  vstate,
  subtitlesWords,
  seekingRef,
}: {
  vstate: VideoPlayerState
  subtitlesWords?: SubtitleWord[] | null
  seekingRef: React.MutableRefObject<boolean>
}) {
  const controlsRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)

  const hasCaptions = !!subtitlesWords && subtitlesWords.length > 0
  const { currentTime, absoluteTime, duration, showControls, showCaptions, speed, SPEEDS } = vstate

  const visibleWords = useMemo(() => {
    if (!showCaptions || !hasCaptions || !subtitlesWords) return []
    // Субтитры индексированы по абсолютному времени исходного файла — не по currentTime,
    // которое для AI-фрагментов отсчитывается от начала обрезанного окна.
    return getVisibleSubtitleWords(subtitlesWords, absoluteTime)
  }, [showCaptions, hasCaptions, subtitlesWords, absoluteTime])

  const fmt = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`

  // Native touch → stopPropagation prevents the lightbox FSM from seeing events on controls
  useEffect(() => {
    const ctrlEl = controlsRef.current
    const barEl  = progressRef.current
    if (!ctrlEl || !barEl) return

    const stopS = (e: TouchEvent) => e.stopPropagation()
    const stopM = (e: TouchEvent) => e.stopPropagation()
    ctrlEl.addEventListener('touchstart', stopS, { passive: true })
    ctrlEl.addEventListener('touchmove',  stopM, { passive: false })

    let seeking = false
    const bStart = (e: TouchEvent) => {
      e.stopPropagation()
      seeking = true
      seekingRef.current = true   // tells the gesture FSM to stand down
      vstate.doSeek(e.touches[0].clientX, barEl)
    }
    const bMove  = (e: TouchEvent) => { e.stopPropagation(); e.preventDefault(); if (seeking) vstate.doSeek(e.touches[0].clientX, barEl) }
    const bEnd   = (e: TouchEvent) => {
      e.stopPropagation()
      seeking = false
      seekingRef.current = false  // release the FSM
    }
    barEl.addEventListener('touchstart', bStart, { passive: true })
    barEl.addEventListener('touchmove',  bMove,  { passive: false })
    barEl.addEventListener('touchend',   bEnd,   { passive: true })

    return () => {
      ctrlEl.removeEventListener('touchstart', stopS)
      ctrlEl.removeEventListener('touchmove',  stopM)
      barEl.removeEventListener('touchstart',  bStart)
      barEl.removeEventListener('touchmove',   bMove)
      barEl.removeEventListener('touchend',    bEnd)
    }
  }, [vstate.doSeek])

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <>
      {/* Субтитры — если включены, всегда видны независимо от showControls (не прячутся
          вместе с панелью управления). Когда controls скрыты, съезжают ближе к нижнему краю. */}
      {showCaptions && visibleWords.length > 0 && (
        <div style={{
          position: 'absolute', left: 0, right: 0, zIndex: 5,
          bottom: showControls ? 92 : 'max(20px, env(safe-area-inset-bottom))',
          display: 'flex', justifyContent: 'center',
          paddingLeft: 16, paddingRight: 16,
          pointerEvents: 'none',
          transition: 'bottom 0.2s ease-out',
        }}>
          <p style={{
            background: 'rgba(0,0,0,0.82)', color: 'white',
            padding: '5px 12px', borderRadius: 5,
            fontSize: 14, lineHeight: '1.45', textAlign: 'center',
            maxWidth: '90%', margin: 0,
          }}>
            {visibleWords.map((w, i) => (
              <span key={`${w.startTime}-${i}`}>{w.word}{i < visibleWords.length - 1 ? ' ' : ''}</span>
            ))}
          </p>
        </div>
      )}

      {/* display:none keeps DOM/refs/listeners alive across hide↔show cycles. */}
      <div
        ref={controlsRef}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 5,
          display: showControls ? 'block' : 'none',
          pointerEvents: 'none',
        }}
      >
        {/* Gradient + seek-bar row */}
        <div style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.35) 70%, transparent 100%)',
          paddingTop: 48, paddingLeft: 16, paddingRight: 16,
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          pointerEvents: 'auto',
        }}>
          {/* Progress row: time | seek bar | duration | speed | CC */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'white', fontSize: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>
              {fmt(currentTime)}
            </span>

            {/* Seek bar — touch events handled via native listeners above */}
            <div
              ref={progressRef}
              style={{ flex: 1, height: 36, display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}
              onClick={(e) => { e.stopPropagation(); if (progressRef.current) vstate.doSeek(e.clientX, progressRef.current) }}
            >
              <div style={{ position: 'absolute', left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.28)', borderRadius: 2 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'white', borderRadius: 2, position: 'relative' }}>
                  <div style={{
                    position: 'absolute', right: -6, top: '50%', transform: 'translateY(-50%)',
                    width: 13, height: 13, borderRadius: '50%', background: 'white',
                    boxShadow: '0 1px 5px rgba(0,0,0,0.7)',
                  }} />
                </div>
              </div>
            </div>

            <span style={{ color: 'rgba(255,255,255,0.60)', fontSize: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>
              {fmt(duration)}
            </span>

            <button
              onClick={(e) => { e.stopPropagation(); vstate.cycleSpeed(SPEEDS) }}
              style={{
                background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.32)',
                borderRadius: 5, padding: '3px 7px', color: 'white',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                minWidth: 34, textAlign: 'center',
              }}
              aria-label="Скорость воспроизведения"
            >
              {speed === 1 ? '1×' : `${speed}×`}
            </button>

            {hasCaptions && (
              <button
                onClick={(e) => { e.stopPropagation(); vstate.toggleCaptions() }}
                style={{
                  background: showCaptions ? 'rgba(255,255,255,0.20)' : 'none',
                  border: '1.5px solid rgba(255,255,255,0.40)',
                  borderRadius: 5, padding: '3px 7px',
                  color: showCaptions ? 'white' : 'rgba(255,255,255,0.45)',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  transition: 'all 0.15s',
                }}
                aria-label={showCaptions ? 'Выключить субтитры' : 'Включить субтитры'}
              >
                CC
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Mobile helpers (pure, outside component) ──────────────────────────────────

/** Smooth rubber-band past a hard limit. Returns position that slows down beyond ±limit. */
function rubberBand(x: number, limit: number): number {
  if (limit <= 0 || Math.abs(x) <= limit) return x
  const sign = x > 0 ? 1 : -1
  const excess = Math.abs(x) - limit
  // Hyperbolic resistance: excess / (1 + excess * k) — approaches limit + 1/k asymptote
  return sign * (limit + excess / (1 + excess * 0.016))
}

/** Cubic ease-out (fast start, slow finish). */
function easeOut3(t: number): number {
  return 1 - Math.pow(1 - Math.min(1, t), 3)
}

// ── Mobile: MobileMediaLightbox ────────────────────────────────────────────────
//
// Architecture — fully DOM-driven, no React state for positions:
//  • trackRef + trackPos:  carousel slide position (px from rest = -100vw)
//  • zoomRef + zoomPos:    zoom/pan transform on current slide
//  • Both animated via RAF loops — zero React re-renders during animation
//  • React state: `index` only (updates after slide animation completes)
//
// Panels: always 3 (left, center, right). Out-of-bounds slots = null = black.
//   → No clone at edges (was the cause of "same image on both sides" bug).
//
// Rubber-band: absolute formula, not delta-based → no sudden jump at boundary.
//
// Pan overflow → carousel: when zoomed and panning past the boundary,
//   excess horizontal motion moves the carousel at 0.3× speed.
//   On release: if track moved far enough, navigate; otherwise spring back.

export const MobileMediaLightbox = memo(function MobileMediaLightbox({
  items,
  initialIndex = 0,
  onClose,
}: {
  items: MediaLightboxItem[]
  initialIndex?: number
  onClose: () => void
}) {
  // Only React state: the committed slide index (updated after animation)
  const [index, setIndex] = useState(initialIndex)

  // Refs for logical indices
  const indexRef    = useRef(index);    indexRef.current    = index
  const lenRef      = useRef(items.length); lenRef.current  = items.length
  const pendingIdx  = useRef(initialIndex) // advances immediately on navigate()

  // DOM refs
  const containerRef   = useRef<HTMLDivElement>(null)
  const escapeReturnFocus = useRef<HTMLElement | null>(null)
  const backdropRef    = useRef<HTMLDivElement>(null)
  const dismissWrapRef = useRef<HTMLDivElement>(null)
  const trackRef       = useRef<HTMLDivElement>(null)
  // zoomRef points to the ACTIVE panel's zoom container (updated on navigate).
  // NOT a fixed DOM ref — we reassign .current as the user swipes between panels.
  const zoomRef        = useRef<HTMLDivElement | null>(null)
  // Overlay: close button + dots + video controls (fades during dismiss gesture)
  const overlayRef     = useRef<HTMLDivElement>(null)
  // Seeking flag: set by MobileVideoControls seek bar; blocks gesture FSM while true
  const seekingRef     = useRef(false)

  // Per-panel refs (length = items.length). Populated via JSX callback refs.
  // Video elements persist forever — no re-mount on navigation (fixes iOS re-load bug).
  const panelZoomRefs  = useRef<(HTMLDivElement  | null)[]>([])
  const panelVideoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // Live position state (not React — bypasses re-render for 60fps)
  const trackPos  = useRef(0)   // px offset from rest (-100vw base)
  const dismissY  = useRef(0)   // vertical offset during dismiss gesture
  const zoomPos   = useRef({ scale: 1, tx: 0, ty: 0 })

  // Track animation (RAF-based easing)
  const trackRaf  = useRef<number | null>(null)
  const trackAnim = useRef({ active: false, from: 0, to: 0, startMs: 0, dur: 300,
                             onDone: undefined as (() => void) | undefined })

  // Zoom inertia RAF
  const zoomRaf = useRef<number | null>(null)
  const zoomVel = useRef({ vx: 0, vy: 0, active: false })

  // Touch velocity history (last 100ms)
  const velHist = useRef<Array<{ x: number; y: number; t: number }>>([])

  // Gesture FSM
  const g = useRef({
    mode:       'idle'   as 'idle' | 'swipe' | 'pan' | 'pinch' | 'dismiss',
    zoomLocked: false,   // true once scale > 1; clears on pinch-out to 1×
    dir:        'none'   as 'none' | 'h' | 'v',
    startX: 0, startY: 0,
    panTx:  0, panTy:  0,
    pinchMidX: 0, pinchMidY: 0, pinchDist: 1,
    pinchScale: 1, pinchTx: 0, pinchTy: 0,
  })

  // ── DOM writers ──────────────────────────────────────────────────────────────
  const applyTrack = useCallback((offset: number) => {
    trackPos.current = offset
    const el = trackRef.current
    // Base = -pendingIdx * 100vw; offset = drag delta in px.
    // Works for any number of panels (all-panels architecture).
    if (el) el.style.transform = `translateX(calc(${-pendingIdx.current * 100}vw + ${offset}px))`
  }, [])

  const applyZoom = useCallback((tx: number, ty: number, s: number, transition = '') => {
    zoomPos.current = { tx, ty, scale: s }
    const el = zoomRef.current
    if (!el) return
    el.style.transition = transition
    el.style.transform = (s === 1 && tx === 0 && ty === 0) ? '' : `translate(${tx}px, ${ty}px) scale(${s})`
  }, [])

  const applyDismiss = useCallback((y: number, transition = '') => {
    dismissY.current = y
    const wEl = dismissWrapRef.current
    const bEl = backdropRef.current
    const oEl = overlayRef.current
    const H   = window.innerHeight
    if (wEl) {
      wEl.style.transition = transition
      wEl.style.transform  = y === 0 ? '' : `translateY(${y}px)`
    }
    if (bEl) {
      bEl.style.transition = transition
      bEl.style.background = `rgba(0,0,0,${Math.max(0, 1 - Math.abs(y) / (H * 0.45))})`
    }
    // Controls overlay fades out as the media moves away (starts fading immediately,
    // reaches 0 at ~30% of screen height so controls vanish before full dismiss)
    if (oEl) {
      oEl.style.opacity = String(Math.max(0, 1 - Math.abs(y) / (H * 0.30)))
    }
  }, [])

  const getBounds = useCallback((s: number) => {
    const W = window.innerWidth, H = window.innerHeight
    return { maxTx: Math.max(0, (s - 1) * W / 2), maxTy: Math.max(0, (s - 1) * H / 2) }
  }, [])

  // ── Cancel helpers ────────────────────────────────────────────────────────────
  const cancelZoomInertia = useCallback(() => {
    if (zoomRaf.current !== null) { cancelAnimationFrame(zoomRaf.current); zoomRaf.current = null }
    zoomVel.current.active = false
  }, [])

  // Freeze track animation at current visual position (for new-touch interruption)
  const cancelTrackAnim = useCallback(() => {
    if (trackRaf.current !== null) { cancelAnimationFrame(trackRaf.current); trackRaf.current = null }
    const a = trackAnim.current
    if (a.active) {
      const t = easeOut3((performance.now() - a.startMs) / a.dur)
      applyTrack(a.from + (a.to - a.from) * t)
      a.active = false
    }
  }, [applyTrack])

  // ── Track animation (RAF easing) ──────────────────────────────────────────────
  const animateTrackTo = useCallback((target: number, dur = 300, onDone?: () => void) => {
    cancelTrackAnim()
    const from = trackPos.current
    if (Math.abs(from - target) < 0.5) { applyTrack(target); onDone?.(); return }
    const startMs = performance.now()
    trackAnim.current = { active: true, from, to: target, startMs, dur, onDone }
    const tick = (now: number) => {
      if (!trackAnim.current.active) return
      const t = (now - startMs) / dur
      applyTrack(from + (target - from) * easeOut3(t))
      if (t >= 1) { trackAnim.current.active = false; onDone?.(); return }
      trackRaf.current = requestAnimationFrame(tick)
    }
    trackRaf.current = requestAnimationFrame(tick)
  }, [applyTrack, cancelTrackAnim])

  // ── Navigate ─────────────────────────────────────────────────────────────────
  //
  // All-panels architecture: every item has a permanent DOM slot.
  // Navigation = pure CSS translateX — no React re-render, no media re-mount.
  //
  // Visual continuity across the pendingIdx base change:
  //   Before: translateX(-oldIdx * W + trackPos)
  //   After:  translateX(-newIdx * W + initOffset)
  //   → initOffset = dir * W + trackPos   (so both expressions are equal)
  //
  const navigate = useCallback((dir: -1 | 1) => {
    const oldIdx = pendingIdx.current
    const newIdx = oldIdx + dir
    if (newIdx < 0 || newIdx >= lenRef.current) {
      animateTrackTo(0, 280)
      return
    }

    // ── Level 1: pause + rewind the video we're leaving ─────────────────────────
    const prevVid = panelVideoRefs.current[oldIdx]
    if (prevVid) {
      try { prevVid.pause() } catch (_) {}
      prevVid.currentTime = 0
    }

    // ── Level 2: immediately null mobileVideoRef ──────────────────────────────────
    // The hook's onMeta/onPlay closures check (v === videoRef.current) before playing.
    // Nullifying here means any pending media events fired during the 300ms animation
    // (e.g. loadedmetadata → v.play()) will fail the guard and be suppressed.
    mobileVideoRef.current = null

    // Reset zoom state for the panel we're leaving
    g.current.zoomLocked = false
    const prevZoom = panelZoomRefs.current[oldIdx]
    if (prevZoom) { prevZoom.style.transition = ''; prevZoom.style.transform = '' }
    zoomPos.current = { scale: 1, tx: 0, ty: 0 }

    // Point zoomRef to the destination panel immediately (applyZoom, onStart use it)
    zoomRef.current = panelZoomRefs.current[newIdx] ?? null

    // Advance pendingIdx BEFORE calling applyTrack so the new base is used
    pendingIdx.current = newIdx

    // Re-anchor trackPos so the visual position is unchanged under the new base
    const W = window.innerWidth
    const initOffset = dir * W + trackPos.current
    applyTrack(initOffset)  // DOM sync — no jump

    // Animate to center (offset 0 = newIdx panel centered), then commit React index
    animateTrackTo(0, 300, () => {
      // Level 2 continued: now restore mobileVideoRef to the correct panel
      mobileVideoRef.current = panelVideoRefs.current[newIdx] ?? null
      flushSync(() => setIndex(newIdx))
      // No applyTrack(0) needed — we're already at 0
    })
  }, [animateTrackTo, applyTrack])

  // Instantly complete any in-progress slide animation (called on new touchstart)
  const completeNavigation = useCallback(() => {
    if (!trackAnim.current.active) return
    cancelTrackAnim()
    const target = pendingIdx.current
    // Sync logical refs to the destination panel
    zoomRef.current         = panelZoomRefs.current[target] ?? null
    mobileVideoRef.current  = panelVideoRefs.current[target] ?? null
    indexRef.current = target
    setIndex(target)
    applyTrack(0)
  }, [cancelTrackAnim, applyTrack])

  // ── Zoom pan inertia + spring ──────────────────────────────────────────────────
  const startZoomInertia = useCallback((vx0: number, vy0: number) => {
    cancelZoomInertia()
    zoomVel.current = { vx: vx0, vy: vy0, active: true }
    const FRIC = 0.935, SK = 0.14, SD = 0.72
    const tick = () => {
      if (!zoomVel.current.active) return
      const { vx, vy } = zoomVel.current
      const { tx, ty, scale } = zoomPos.current
      const { maxTx, maxTy } = getBounds(scale)
      const outX = tx > maxTx ? tx - maxTx : tx < -maxTx ? tx + maxTx : 0
      const outY = ty > maxTy ? ty - maxTy : ty < -maxTy ? ty + maxTy : 0
      const damp = (outX || outY) ? SD : FRIC
      const nvx = (vx - outX * SK) * damp
      const nvy = (vy - outY * SK) * damp
      const ntx = tx + nvx, nty = ty + nvy
      applyZoom(ntx, nty, scale)
      zoomVel.current.vx = nvx; zoomVel.current.vy = nvy
      const inB = ntx >= -maxTx && ntx <= maxTx && nty >= -maxTy && nty <= maxTy
      if (Math.sqrt(nvx*nvx + nvy*nvy) < 0.2 && inB) { zoomVel.current.active = false; return }
      zoomRaf.current = requestAnimationFrame(tick)
    }
    zoomRaf.current = requestAnimationFrame(tick)
  }, [applyZoom, getBounds, cancelZoomInertia])

  // ── Reset zoom on slide change ─────────────────────────────────────────────────
  useEffect(() => {
    pendingIdx.current = index
    cancelZoomInertia()
    applyZoom(0, 0, 1)
    g.current.zoomLocked = false
    g.current.mode = 'idle'
    g.current.dir  = 'none'
  }, [index, applyZoom, cancelZoomInertia])

  // ── Scroll lock ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  useEffect(() => () => { cancelZoomInertia(); cancelTrackAnim() }, [cancelZoomInertia, cancelTrackAnim])

  // ── Level 4: universal 'play' guard on all panel videos ───────────────────────
  // This is the last-resort safety net. No matter how a video element fires 'play'
  // (autoplay policy, stale async events, iOS Safari quirks, etc.), if it is not
  // the currently active panel video we immediately pause it so the user never
  // hears audio from a slide they are not looking at.
  useEffect(() => {
    const cleanup: (() => void)[] = []
    panelVideoRefs.current.forEach(el => {
      if (!el) return
      const guard = () => {
        if (el !== mobileVideoRef.current) {
          try { el.pause() } catch (_) {}
        }
      }
      el.addEventListener('play', guard, { passive: true })
      cleanup.push(() => el.removeEventListener('play', guard))
    })
    return () => cleanup.forEach(fn => fn())
  // Run once on mount — panelVideoRefs is populated by then via ref callbacks
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Touch handlers ────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const d2 = (t: TouchList) => { const dx = t[1].clientX-t[0].clientX, dy = t[1].clientY-t[0].clientY; return Math.sqrt(dx*dx+dy*dy) }
    const m2 = (t: TouchList) => ({ x: (t[0].clientX+t[1].clientX)/2, y: (t[0].clientY+t[1].clientY)/2 })

    const getVel = () => {
      const h = velHist.current
      if (h.length < 2) return { vx: 0, vy: 0 }
      const a = h[0], b = h[h.length-1], dt = b.t - a.t
      if (dt < 5) return { vx: 0, vy: 0 }
      return { vx: (b.x-a.x)/dt*16, vy: (b.y-a.y)/dt*16 }
    }

    const onStart = (e: TouchEvent) => {
      // Seek bar is in progress — let MobileVideoControls own this touch entirely
      if (seekingRef.current) return

      // Reset auto-hide timer whenever user touches screen (if controls are visible)
      resetHideTimerRef.current()

      const gr = g.current
      completeNavigation()    // finish any in-progress slide instantly
      cancelZoomInertia()
      if (zoomRef.current) zoomRef.current.style.transition = ''
      // Snap dismiss wrapper back to neutral on new touch (interrupts spring-back animation)
      if (dismissWrapRef.current) { dismissWrapRef.current.style.transition = ''; dismissWrapRef.current.style.transform = '' }
      if (backdropRef.current)    { backdropRef.current.style.transition = '';    backdropRef.current.style.background = '#000' }
      // Clear any lingering transition before snapping overlay opacity back to 1
      if (overlayRef.current)     { overlayRef.current.style.transition = ''; overlayRef.current.style.opacity = '1' }
      dismissY.current = 0
      velHist.current = []
      gr.dir = 'none'
      if (e.touches.length === 1) {
        gr.startX = e.touches[0].clientX; gr.startY = e.touches[0].clientY
        gr.panTx  = zoomPos.current.tx;   gr.panTy  = zoomPos.current.ty
        gr.mode   = gr.zoomLocked ? 'pan' : 'idle'
      }
      if (e.touches.length >= 2) {
        cancelTrackAnim()
        gr.mode = 'pinch'; gr.zoomLocked = true
        const m = m2(e.touches)
        gr.pinchMidX = m.x; gr.pinchMidY = m.y
        gr.pinchDist  = d2(e.touches)
        gr.pinchScale = zoomPos.current.scale
        gr.pinchTx    = zoomPos.current.tx
        gr.pinchTy    = zoomPos.current.ty
      }
    }

    const onMove = (e: TouchEvent) => {
      // Seek bar owns this touch — do not process as a gesture
      if (seekingRef.current) return

      // Finger still on screen → reset the auto-hide countdown (controls stay while touching)
      resetHideTimerRef.current()

      const gr = g.current
      const W = window.innerWidth, H = window.innerHeight

      // ── Pinch-to-zoom ─────────────────────────────────────────────────────
      if (e.touches.length >= 2 && gr.mode === 'pinch') {
        e.preventDefault()
        const nd = d2(e.touches)
        const ns = Math.max(1, Math.min(8, gr.pinchScale * (nd / gr.pinchDist)))
        const m  = m2(e.touches)
        const r  = ns / gr.pinchScale
        // Focal-point (caparzo): tx₁ = m₁.x − W/2 − (m₀.x − W/2 − tx₀) × (s₁/s₀)
        applyZoom(
          m.x - W/2 - (gr.pinchMidX - W/2 - gr.pinchTx) * r,
          m.y - H/2 - (gr.pinchMidY - H/2 - gr.pinchTy) * r,
          ns,
        )
        return
      }

      if (e.touches.length !== 1 || gr.mode === 'pinch') return
      const cx = e.touches[0].clientX, cy = e.touches[0].clientY
      const dx = cx - gr.startX, dy = cy - gr.startY

      const now = performance.now()
      velHist.current.push({ x: cx, y: cy, t: now })
      velHist.current = velHist.current.filter(v => now - v.t < 100)

      // ── Pan (when zoomed) ──────────────────────────────────────────────────
      if (gr.zoomLocked || zoomPos.current.scale > 1.05) {
        e.preventDefault()
        gr.mode = 'pan'
        const { maxTx, maxTy } = getBounds(zoomPos.current.scale)
        const rawTx = gr.panTx + dx, rawTy = gr.panTy + dy
        // Absolute rubber-band: smooth, no jump at boundary (unlike delta-based formula)
        applyZoom(rubberBand(rawTx, maxTx), rubberBand(rawTy, maxTy), zoomPos.current.scale)

        // Overflow beyond the clamped boundary → move carousel at 0.3× (shows intent to navigate)
        const clampedTx = Math.max(-maxTx, Math.min(maxTx, rawTx))
        const overflowX = rawTx - clampedTx  // 0 when in-bounds; nonzero when hitting wall
        if (Math.abs(overflowX) > 2) {
          const curr = pendingIdx.current, len = lenRef.current
          // At edge of the album: more resistance (0.08×) since there's nothing there
          const atEdge = (curr === 0 && overflowX > 0) || (curr === len - 1 && overflowX < 0)
          applyTrack(overflowX * (atEdge ? 0.08 : 0.28))
        } else {
          applyTrack(0)
        }
        return
      }

      // ── Swipe (h) or Dismiss (v) ──────────────────────────────────────────
      if (gr.dir === 'none' && (Math.abs(dx) > 8 || Math.abs(dy) > 8))
        gr.dir = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v'

      if (gr.dir === 'h') {
        e.preventDefault()
        gr.mode = 'swipe'
        const curr = pendingIdx.current, len = lenRef.current
        // Rubber-band at first/last slide (no wrap)
        const atEdge = (curr === 0 && dx > 0) || (curr === len - 1 && dx < 0)
        applyTrack(atEdge ? rubberBand(dx, 0) * 0.22 : dx)

        // Fade overlay (seek bar, close btn) when swiping toward a non-video panel.
        // If the adjacent panel is also a video, keep overlay fully visible.
        const adjIdx  = curr + (dx < 0 ? 1 : -1)
        const adjItem = itemsRef.current[adjIdx]
        const adjIsVid = adjItem?.type === 'video'
        if (!adjIsVid && overlayRef.current) {
          const frac = Math.min(1, Math.abs(dx) / W)
          overlayRef.current.style.opacity = String(1 - frac)
        }
        return
      }

      if (gr.dir === 'v') {
        e.preventDefault()
        gr.mode = 'dismiss'
        applyDismiss(dy)
        return
      }
    }

    const onEnd = (e: TouchEvent) => {
      const gr = g.current

      // ── 2→1 finger: stay in pinch mode — wait for ALL fingers to lift ────
      // Switching to pan here causes the remaining finger to accidentally move the
      // carousel track, and lifting it triggers unwanted navigation.
      // Zoom mode ends only when touches reach 0.
      if (e.touches.length === 1 && gr.mode === 'pinch') return

      // ── Pinch ended (both fingers up) ─────────────────────────────────────
      if (gr.mode === 'pinch') {
        gr.mode = 'idle'
        const { tx, ty, scale } = zoomPos.current
        if (scale < 1.1) {
          applyZoom(0, 0, 1, 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)')
          gr.zoomLocked = false
        } else {
          const { maxTx, maxTy } = getBounds(scale)
          const clTx = Math.max(-maxTx, Math.min(maxTx, tx))
          const clTy = Math.max(-maxTy, Math.min(maxTy, ty))
          if (Math.abs(clTx-tx) > 0.5 || Math.abs(clTy-ty) > 0.5)
            applyZoom(clTx, clTy, scale, 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)')
        }
        return
      }

      // ── Pan ended: zoom inertia + possible navigation from overflow ────────
      if (gr.mode === 'pan') {
        gr.mode = 'idle'
        const { tx, ty, scale } = zoomPos.current

        if (scale <= 1.05) {
          gr.zoomLocked = false
          if (Math.abs(tx) > 0.5 || Math.abs(ty) > 0.5)
            applyZoom(0, 0, 1, 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)')
          animateTrackTo(0, 280)
          return
        }

        const { vx, vy } = getVel()
        const W = window.innerWidth
        const curr = pendingIdx.current, len = lenRef.current
        const trackOff = trackPos.current

        // Decide if overflow pan is enough to trigger navigation
        const flick    = Math.abs(vx) >= 5
        const navThres = flick ? W * 0.10 : W * 0.22

        if (trackOff > navThres && curr > 0) {
          applyZoom(0, 0, 1); gr.zoomLocked = false; navigate(-1)
        } else if (trackOff < -navThres && curr < len - 1) {
          applyZoom(0, 0, 1); gr.zoomLocked = false; navigate(1)
        } else {
          // Spring track back; apply inertia on zoom
          animateTrackTo(0, 280)
          const { maxTx, maxTy } = getBounds(scale)
          const outside = tx > maxTx || tx < -maxTx || ty > maxTy || ty < -maxTy
          if (outside || Math.abs(vx) > 0.4 || Math.abs(vy) > 0.4) startZoomInertia(vx, vy)
        }
        return
      }

      // ── Swipe ended: navigate or snap back ────────────────────────────────
      if (gr.mode === 'swipe') {
        gr.mode = 'idle'
        const W    = window.innerWidth
        const curr = pendingIdx.current, len = lenRef.current
        const dx   = e.changedTouches[0].clientX - gr.startX
        const { vx } = getVel()
        // Flick: fast + at least 15px → navigate at 10% threshold
        // Normal: navigate at 30% threshold (feels responsive, not "halfway")
        const flick     = Math.abs(vx) >= 4 && Math.abs(dx) >= 15
        const threshold = flick ? W * 0.10 : W * 0.30
        // Always restore overlay opacity (navigate() or spring-back; React re-render
        // handles the seek-bar visibility based on new isCurrVideo value)
        if (overlayRef.current) { overlayRef.current.style.transition = 'opacity 0.2s ease-out'; overlayRef.current.style.opacity = '1' }
        if      (dx >  threshold && curr > 0)       navigate(-1)
        else if (dx < -threshold && curr < len - 1) navigate(1)
        else                                        animateTrackTo(0, 280)
        return
      }

      // ── Dismiss ended: fly off-screen or snap back ────────────────────────
      if (gr.mode === 'dismiss') {
        gr.mode = 'idle'
        const H  = window.innerHeight
        const dy = e.changedTouches[0].clientY - gr.startY
        if (Math.abs(dy) > H * 0.15) {
          // Fly off in the direction of the swipe, then close
          const dir    = dy >= 0 ? 1 : -1
          const target = dir * H * 1.2
          const wEl = dismissWrapRef.current
          const bEl = backdropRef.current
          const oEl = overlayRef.current
          if (wEl) { wEl.style.transition = 'transform 0.15s ease-in'; wEl.style.transform = `translateY(${target}px)` }
          if (bEl) { bEl.style.transition = 'background 0.15s ease-in'; bEl.style.background = 'rgba(0,0,0,0)' }
          if (oEl) { oEl.style.transition = 'opacity 0.15s ease-in';    oEl.style.opacity = '0' }
          setTimeout(() => onClose(), 150)
        } else {
          applyDismiss(0, 'transform 0.25s ease-out')
        }
        return
      }

      gr.mode = 'idle'
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove',  onMove,  { passive: false })
    el.addEventListener('touchend',   onEnd,   { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove',  onMove)
      el.removeEventListener('touchend',   onEnd)
    }
  }, [navigate, completeNavigation, applyZoom, applyTrack, applyDismiss, getBounds,
      cancelZoomInertia, cancelTrackAnim, animateTrackTo, startZoomInertia, onClose])

  // Subtitles for center panel (enables AI video player when available)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading: subLoading, data: subData } = useSubtitles(items[index])

  // ── Mobile video player hook (always called — hooks must be unconditional) ──
  // mobileVideoRef is a MUTABLE ref — navigate() reassigns .current to the active video element.
  // The hook re-initialises when `index` changes (its itemIndex dep), by which point
  // mobileVideoRef.current already points to the new panel's <video> element.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null)
  // AI-обрезанный фрагмент (если есть) для видео текущей панели — передаётся в хук, чтобы
  // AI-вложения играли в едином мобильном плеере вместо старого AIVideoPlayer.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const aiWindow = useMemo(() => {
    const m = items[index]?.aiMetadata
    return m ? computeAiWindow(m) : null
  }, [items, index])
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const vstate = useMobileVideoPlayer(mobileVideoRef as React.RefObject<HTMLVideoElement | null>, index, aiWindow)

  // Stable ref-mirrors so the gesture FSM closure can read latest values without
  // being added to the useEffect deps (which would re-register all touch listeners).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const itemsRef = useRef(items)
  itemsRef.current = items
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const resetHideTimerRef = useRef<() => void>(() => {})
  resetHideTimerRef.current = vstate.resetHideTimer
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const revealControlsRef = useRef<() => void>(() => {})
  revealControlsRef.current = vstate.revealControls

  // Десктоп (лайтбокс открыт мышью): Esc закрывает, движение мыши показывает скрытые контролы.
  // pointerType === 'mouse' — на тач-устройствах эмулированные события не должны возвращать
  // панель сразу после того, как её скрыли тапом.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const el = containerRef.current
    const outside = focusOutsideLightbox()
    if (outside) escapeReturnFocus.current = outside
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      closeOnEscape(e, onClose, escapeReturnFocus.current)
    }
    const onPointerMove = (e: PointerEvent) => { if (e.pointerType === 'mouse') revealControlsRef.current() }
    window.addEventListener('keydown', onKey, { capture: true })
    el?.addEventListener('pointermove', onPointerMove)
    const releaseSheet = keepSheetOpen(el)
    return () => {
      releaseSheet()
      window.removeEventListener('keydown', onKey, { capture: true })
      el?.removeEventListener('pointermove', onPointerMove)
    }
  }, [onClose])

  if (typeof document === 'undefined') return null

  const currItem    = items[index]
  const isCurrVideo = currItem.type === 'video'

  const panelStyle: React.CSSProperties = {
    width: '100vw',
    // 100dvh = dynamic viewport height: current visible area excluding browser chrome.
    // 100vh on iOS Safari = layout viewport (includes address bar + tab bar space) → video
    // objectFit:contain centres content in an oversized box causing it to fall below screen.
    // Fallback: browsers without dvh support use auto which stretches in this flex context.
    height: '100dvh',
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  }

  return createPortal(
    <div ref={containerRef} data-media-lightbox="true" style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      overflow: 'hidden', touchAction: 'none', userSelect: 'none',
      pointerEvents: 'auto',
    }}>
      {/* Backdrop — opacity fades during vertical dismiss gesture */}
      <div ref={backdropRef} style={{
        position: 'absolute', inset: 0, background: '#000', zIndex: 0,
      }} />

      {/* Dismiss wrapper — translates vertically + scales during dismiss */}
      <div ref={dismissWrapRef} style={{
        position: 'absolute', inset: 0, zIndex: 1,
        transformOrigin: 'center center', willChange: 'transform',
      }}>
        {/* ── Carousel track — ALL panels pre-rendered, DOM-controlled ──
             Each item gets a permanent <div> slot. Navigation = pure translateX.
             Video <video> elements are NEVER unmounted — no re-load, no iOS Safari bug.
             Width = items.length × 100vw; initial transform centres initialIndex panel. */}
        <div ref={trackRef} style={{
          display: 'flex', width: `${items.length * 100}vw`, height: '100%',
          transform: `translateX(${-initialIndex * 100}vw)`,
          willChange: 'transform',
        }}>
          {items.map((item, i) => {
            const isVid = item.type === 'video'
            const isCur = i === index
            return (
              <div key={i} style={panelStyle}>
                {/* Per-panel zoom container.
                    zoomRef.current is updated to point here by navigate() / completeNavigation().
                    JSX callback also keeps it in sync on re-renders. */}
                <div
                  ref={el => {
                    panelZoomRefs.current[i] = el
                    if (i === pendingIdx.current) zoomRef.current = el
                  }}
                  style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transformOrigin: 'center center', willChange: 'transform',
                  }}
                >
                  {isVid ? (
                    // pointer-events:none → touches pass through to tap overlay;
                    // fixes iOS Safari absorbing swipe/zoom events on a playing <video>.
                    // preload="metadata" loads thumbnails for ALL items at open time.
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      ref={el => {
                        panelVideoRefs.current[i] = el
                        // Only set mobileVideoRef to the panel we are CURRENTLY navigated to
                        // (pendingIdx.current). Using initialIndex here caused a bug: after
                        // navigate() nulled mobileVideoRef, the next re-render would restore
                        // it to the old video because !null === true, making the hook play
                        // the invisible video.
                        if (i === pendingIdx.current && el) mobileVideoRef.current = el
                      }}
                      src={item.src}
                      playsInline
                      preload="metadata"
                      poster={VIDEO_BLANK_POSTER}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
                    />
                  ) : (
                    <MediaContent
                      item={item}
                      subLoading={isCur ? subLoading : false}
                      subData={isCur ? subData : null}
                      className="block w-full h-full object-contain pointer-events-auto"
                    />
                  )}
                </div>

                {/* Tap overlay: transparent, sits below buttons (z-index 2 < buttons z-index 4).
                    Tapping empty space → show/hide controls. Tapping a button → button handles it.
                    Only active for the current panel (others are off-screen). */}
                {isVid && isCur && (
                  <div
                    style={{ position: 'absolute', inset: 0, zIndex: 2 }}
                    onClick={vstate.handleVideoAreaTap}
                  />
                )}

                {/* ── Buffering spinner — shown while loading, centred in panel ── */}
                {isVid && isCur && vstate.isLoading && (
                  <>
                    <style>{`@keyframes _vp-spin { to { transform: rotate(360deg) } }`}</style>
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)', zIndex: 4, pointerEvents: 'none',
                    }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: '50%',
                        border: '3.5px solid rgba(255,255,255,0.25)',
                        borderTopColor: 'rgba(255,255,255,0.92)',
                        animation: '_vp-spin 0.8s linear infinite',
                      }} />
                    </div>
                  </>
                )}

                {/* ── Play / skip buttons — move WITH the video (in carousel) ──
                     Always mounted when video is current + not loading so that
                     fade-out works (opacity transition needs element in DOM).
                     Centred at 50% height → independent of subtitle block height.
                     alignItems: center ensures all three buttons share the same
                     vertical midline despite having different heights (56 vs 72 px).
                     opacity + transition = smooth fade on showControls toggle.
                     pointerEvents on each button switches off when hidden so
                     invisible buttons do not intercept taps. */}
                {isVid && isCur && !vstate.isLoading && (
                  <div style={{
                    position: 'absolute', top: '50%', left: 0, right: 0,
                    transform: 'translateY(-50%)',
                    zIndex: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 40,
                    pointerEvents: 'none',
                    opacity: vstate.showControls ? 1 : 0,
                    transition: 'opacity 0.3s ease-out',
                  }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); vstate.skip(-10) }}
                      style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)',
                        border: 'none', cursor: 'pointer', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: vstate.showControls ? 'auto' : 'none',
                      }}
                      aria-label="−10 секунд"
                    >
                      <SkipIcon n={10} forward={false} />
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); vstate.togglePlay() }}
                      style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)',
                        border: 'none', cursor: 'pointer', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        pointerEvents: vstate.showControls ? 'auto' : 'none',
                      }}
                      aria-label={vstate.isPlaying ? 'Пауза' : 'Воспроизвести'}
                    >
                      {vstate.isPlaying ? (
                        <svg width="32" height="32" fill="white" viewBox="0 0 24 24">
                          <rect x="6" y="4" width="4" height="16" rx="1.5"/>
                          <rect x="14" y="4" width="4" height="16" rx="1.5"/>
                        </svg>
                      ) : (
                        <svg width="32" height="32" fill="white" viewBox="0 0 24 24" style={{ marginLeft: 3 }}>
                          <path d="M8 5v14l11-7L8 5z"/>
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); vstate.skip(10) }}
                      style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)',
                        border: 'none', cursor: 'pointer', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: vstate.showControls ? 'auto' : 'none',
                      }}
                      aria-label="+10 секунд"
                    >
                      <SkipIcon n={10} forward={true} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {/* NOTE: close button, dots and video controls are rendered OUTSIDE dismissWrapRef
            (as siblings in containerRef) so they do NOT move during the dismiss swipe gesture */}
      </div>

      {/* ── Close button — NEVER fades, lives outside overlayRef ─────────────────
           Separate from overlayRef so opacity changes on overlayRef (dismiss fade,
           horizontal-swipe fade) never affect the close button's glassmorphism. ── */}
      <button onClick={onClose} aria-label="Закрыть" style={{
        position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', right: 16,
        zIndex: 12, color: 'white', background: 'rgba(0,0,0,0.52)',
        backdropFilter: 'blur(8px)',
        border: 'none', borderRadius: '50%', width: 36, height: 36,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}>
        <X size={18} />
      </button>

      {/* ── Overlay: dots + video seek bar (fades on dismiss and horizontal swipe) ──
           pointer-events:none on wrapper; each child restores its own events. ── */}
      <div ref={overlayRef} style={{
        position: 'absolute', inset: 0, zIndex: 10,
        pointerEvents: 'none',
      }}>
        {/* Dots — shown only when current item is NOT a video (videos have their own controls) */}
        {items.length > 1 && !isCurrVideo && (
          <div style={{
            position: 'absolute',
            bottom: isCurrVideo
              ? 'max(108px, calc(env(safe-area-inset-bottom) + 92px))'
              : 'max(20px, env(safe-area-inset-bottom))',
            left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8,
            pointerEvents: 'none',
            transition: 'bottom 0.2s ease',
          }}>
            {items.map((_, i) => (
              <button key={i} onClick={() => {
                cancelZoomInertia(); cancelTrackAnim()
                // Pause + rewind current video before jumping
                const curVid = panelVideoRefs.current[pendingIdx.current]
                if (curVid) { try { curVid.pause() } catch (_) {}; curVid.currentTime = 0 }
                // Reset zoom on current panel
                const curZoom = panelZoomRefs.current[pendingIdx.current]
                if (curZoom) { curZoom.style.transition = ''; curZoom.style.transform = '' }
                g.current.zoomLocked = false; zoomPos.current = { scale: 1, tx: 0, ty: 0 }
                // Update all refs to destination panel
                zoomRef.current        = panelZoomRefs.current[i] ?? null
                mobileVideoRef.current = panelVideoRefs.current[i] ?? null
                pendingIdx.current = i; indexRef.current = i
                setIndex(i); applyTrack(0)
              }} style={{
                pointerEvents: 'auto',
                width: i === index ? 20 : 8, height: 8, borderRadius: 4,
                background: i === index ? 'white' : 'rgba(255,255,255,0.35)',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'all 0.2s ease',
              }} />
            ))}
          </div>
        )}

        {/* Video controls */}
        {isCurrVideo && <MobileVideoControls vstate={vstate} subtitlesWords={subData} seekingRef={seekingRef} />}
      </div>
    </div>,
    document.body
  )
})

// ── Conversion helpers ────────────────────────────────────────────────────────

type AttLike = {
  id: number
  type: number
  content?: { url?: string | null; staticUrl?: string | null; name?: string | null } | null
}

/** Convert a single incident/decision attachment to MediaLightboxItem, or null if not viewable */
export function incidentAttToItem(att: AttLike): MediaLightboxItem | null {
  const src = att.content?.url ?? att.content?.staticUrl ?? ''
  if (!src) return null
  const type: 'image' | 'video' | null = att.type === 1 ? 'image' : att.type === 2 ? 'video' : null
  if (!type) return null
  return { id: att.id, src, type, alt: att.content?.name ?? undefined }
}

/** Build a MediaLightboxItem array from an attachment list (filters non-viewable) */
export function buildLightboxItems(atts: AttLike[]): MediaLightboxItem[] {
  return atts.flatMap(a => { const item = incidentAttToItem(a); return item ? [item] : [] })
}
